import React, { useEffect, useState, useCallback } from "react";
import {
	Modal,
	Paper,
	Box,
	Typography,
	TextField,
	FormControl,
	Select,
	InputLabel,
	MenuItem,
	Button,
	CircularProgress,
	InputAdornment,
	Divider,
	useTheme,
	IconButton,
	Grid,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { z } from "zod";
import axios from "axios";
import InputMask from "react-input-mask";
import useStyles from "./styles"; // Certifique-se que o caminho está correto
import api from "../../../services/api"; // Certifique-se que o caminho está correto

// --- Schemas de Validação ---
const schemas = {
	business: z.object({
		cnpj: z.string().min(1, "CNPJ é obrigatório"),
		razaoSocial: z.string().min(1, "Razão Social é obrigatória"),
		nomeFantasia: z.string().min(1, "Nome Fantasia é obrigatório"),
	}),
	cnpj: z.string().regex(/^\d{14}$/, "CNPJ inválido"),
	cep: z.string().regex(/^\d{5}-\d{3}$/, "CEP inválido"),
	telefone: z.string().regex(/^\(\d{2}\) \d{4}-\d{4}$/, "Telefone inválido"),
	celular: z.string().regex(/^\(\d{2}\) \d{5}-\d{4}$/, "Celular inválido"),
	email: z.string().email("Email inválido"),
};

// --- Componente Auxiliar para reduzir repetição no JSX ---
const FormTextField = ({ span = 6, mask, ...props }) => (
	<Grid item xs={12} sm={span}>
		{mask ? (
			<InputMask
				mask={mask}
				value={props.value}
				onChange={props.onChange}
				onBlur={props.onBlur}
				disabled={props.disabled}
			>
				{(inputProps) => (
					<TextField
						{...inputProps}
						{...props}
						fullWidth
						variant="filled"
						size="small"
						autoComplete="off"
					/>
				)}
			</InputMask>
		) : (
			<TextField
				{...props}
				fullWidth
				variant="filled"
				size="small"
				autoComplete="off"
			/>
		)}
	</Grid>
);

export default function CadastroCliente({
	openModal,
	setOpenModal,
	handleNewClient,
	setSnackbar,
}) {
	const theme = useTheme();
	const styles = useStyles(theme);

	// --- Estados ---
	const initialFormState = {
		cnpj: "",
		razaoSocial: "",
		nomeFantasia: "",
		as: "",
		cep: "",
		estado: "",
		cidade: "",
		bairro: "",
		logradouro: "",
		numero: "",
		complemento: "",
		telefone: "",
		email: "",
		plano: "",
		diaDePagamento: "",
	};

	const [formData, setFormData] = useState(initialFormState);
	// Se houver inputs para valor/reajuste no futuro, adicione aqui.
	// Mantive a lógica de envio, mas removi estados não usados na UI para limpar.
	const [values] = useState({ valor: "", reajuste: "" });

	const [errors, setErrors] = useState({});
	const [loadings, setLoadings] = useState({
		general: false,
		cep: false,
		asn: false,
	});
	const [asValidado, setAsValidado] = useState(true);

	// --- Efeitos ---
	useEffect(() => {
		if (openModal) {
			setFormData(initialFormState);
			setErrors({});
			setAsValidado(true);
		}
	}, [openModal]);

	// --- Manipuladores de Mudança ---
	const handleInputChange = useCallback(
		(e) => {
			const { name, value } = e.target;

			// Lógica específica para ASN (apenas números e invalida checagem anterior)
			if (name === "as") {
				setAsValidado(false);
				const nums = value?.match(/\d+/g)?.join("") || "";
				setFormData((prev) => ({ ...prev, [name]: nums }));
			} else {
				setFormData((prev) => ({ ...prev, [name]: value }));
			}

			// Limpa erro ao digitar
			if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

			// Gatilho automático para CNPJ
			const rawValue = value.replace(/[^\d]/g, "");
			if (name === "cnpj" && rawValue.length === 14) {
				fetchBrByteData(rawValue);
			}
		},
		[errors],
	);

	// --- Ações de API e Validação ---
	const toggleLoading = (key, status) =>
		setLoadings((prev) => ({ ...prev, [key]: status }));

	async function fetchCepData(cep) {
		toggleLoading("cep", true);
		try {
			const { data } = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
			if (data.erro) throw new Error("CEP não encontrado");

			setFormData((prev) => ({
				...prev,
				estado: data.uf,
				cidade: data.localidade,
				bairro: data.bairro,
				logradouro: data.logradouro,
			}));
			// Limpa erros desses campos
			setErrors((prev) => ({
				...prev,
				estado: "",
				cidade: "",
				bairro: "",
				logradouro: "",
			}));
		} catch (error) {
			console.error(error);
		} finally {
			toggleLoading("cep", false);
		}
	}

	async function validateAsn(as) {
		if (!as) return;
		toggleLoading("asn", true);
		try {
			const { data } = await axios.get(
				"https://stat.ripe.net/data/as-overview/data.json",
				{ params: { resource: as } },
			);
			if (!data?.data?.announced) throw new Error("ASN não encontrado");
			setAsValidado(true);
		} catch (error) {
			setErrors((prev) => ({ ...prev, as: "ASN inválido ou não encontrado" }));
			setAsValidado(false);
		} finally {
			toggleLoading("asn", false);
		}
	}

	async function fetchBrByteData(cnpj) {
		toggleLoading("general", true);
		try {
			const { data } = await api.post("/cliente/brbyte", { cnpj });
			const c = data.cliente;
			setFormData((prev) => ({
				...prev,
				cnpj: cnpj, // Mantém formatado ou raw dependendo da preferencia
				razaoSocial: c.client_name,
				nomeFantasia: c.client_lastname,
				as: c.client_observations,
				cep: c.cli_addr_zipcode,
				estado: c.cli_addr_state,
				cidade: c.cli_addr_province,
				bairro: c.cli_addr_neighborhood,
				logradouro: c.cli_addr_address,
				numero: c.cli_addr_number,
				complemento: c.cli_addr_completation,
				telefone: c.client_phones,
				email: c.client_emails,
			}));
		} catch (error) {
			setSnackbar({
				children: "Erro ao buscar dados do CNPJ",
				severity: "error",
			});
		} finally {
			toggleLoading("general", false);
		}
	}

	const handleBlur = (e) => {
		const { name, value } = e.target;
		if (!value) return;

		try {
			if (name === "cnpj") schemas.cnpj.parse(value.replace(/[^\d]/g, ""));
			if (name === "cep") {
				schemas.cep.parse(value);
				fetchCepData(value);
			}
			if (name === "telefone") {
				const clean = value.replace(/\s+/g, "");
				clean.length === 13
					? schemas.telefone.parse(value.trim())
					: schemas.celular.parse(value);
			}
			if (name === "email") schemas.email.parse(value);
			if (name === "as") validateAsn(value);

			setErrors((prev) => ({ ...prev, [name]: "" }));
		} catch (err) {
			if (err instanceof z.ZodError) {
				setErrors((prev) => ({ ...prev, [name]: err.errors[0].message }));
			}
		}
	};

	async function handleSubmit() {
		toggleLoading("general", true);
		try {
			schemas.business.parse(formData);

			if (!asValidado && formData.as) {
				throw new Error("Valide o ASN antes de continuar.");
			}

			const payload = {
				...formData,
				diaDePagamento: formData.diaDePagamento?.toString(),
				valor: parseFloat(values.valor || 0),
				reajuste: parseFloat(values.reajuste || 0),
			};

			const response = await api.post("/createCliente", payload);

			handleNewClient({
				id: response.data.id,
				...payload,
				status: response.data.status,
				ClientesCadastro: {
					...payload,
					status: response.data.ClientesCadastro?.status,
				},
			});

			setOpenModal(false);
			setSnackbar({
				children: "Cliente cadastrado com sucesso!",
				severity: "success",
			});
		} catch (error) {
			if (error instanceof z.ZodError) {
				const newErrors = {};
				error.errors.forEach((err) => {
					newErrors[err.path[0]] = err.message;
				});
				setErrors(newErrors);
			} else {
				setSnackbar({
					children: error.message || "Erro ao salvar",
					severity: "error",
				});
			}
		} finally {
			toggleLoading("general", false);
		}
	}

	return (
		<Modal
			open={openModal}
			onClose={() => setOpenModal(false)}
			sx={styles.modal}
		>
			<Paper sx={styles.container}>
				{/* Header */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
					}}
				>
					<Typography variant="title" sx={styles.title}>
						Cadastro
					</Typography>
					<IconButton onClick={() => setOpenModal(false)}>
						<CloseOutlinedIcon sx={{ color: theme.palette.white.main }} />
					</IconButton>
				</Box>

				<Box sx={styles.body}>
					<Grid container spacing={1}>
						{/* Dados Empresariais */}
						<Grid item xs={12}>
							<Typography variant="mySubtitle2">Dados empresariais</Typography>
						</Grid>

						<FormTextField
							name="cnpj"
							label="CNPJ"
							mask="99.999.999/9999-99"
							value={formData.cnpj}
							onChange={handleInputChange}
							onBlur={handleBlur}
							error={Boolean(errors.cnpj)}
							helperText={errors.cnpj}
						/>
						<FormTextField
							name="razaoSocial"
							label="Razão Social"
							value={formData.razaoSocial}
							onChange={handleInputChange}
							error={Boolean(errors.razaoSocial)}
							helperText={errors.razaoSocial}
						/>
						<FormTextField
							name="nomeFantasia"
							label="Nome Fantasia"
							value={formData.nomeFantasia}
							onChange={handleInputChange}
							error={Boolean(errors.nomeFantasia)}
							helperText={errors.nomeFantasia}
						/>
						<FormTextField
							name="as"
							label="ASN"
							value={formData.as}
							onChange={handleInputChange}
							onBlur={handleBlur}
							error={Boolean(errors.as)}
							helperText={errors.as}
							disabled={loadings.asn}
						/>

						<Grid item xs={12}>
							<Divider sx={{ my: 1 }} />
						</Grid>

						{/* Endereço */}
						<Grid item xs={12}>
							<Typography variant="mySubtitle2">Endereço</Typography>
						</Grid>

						<FormTextField
							name="cep"
							label="CEP"
							mask="99999-999"
							value={formData.cep}
							onChange={handleInputChange}
							onBlur={handleBlur}
							error={Boolean(errors.cep)}
							helperText={errors.cep}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{loadings.cep && <CircularProgress size={20} />}
									</InputAdornment>
								),
							}}
						/>

						<Grid item xs={12} sm={6}>
							<FormControl
								variant="filled"
								sx={styles.textfield}
								fullWidth
								size="small"
								disabled={loadings.cep}
							>
								<InputLabel>Estado</InputLabel>
								<Select
									name="estado"
									value={formData.estado}
									onChange={handleInputChange}
									error={Boolean(errors.estado)}
								>
									{brazilianStates.map((state) => (
										<MenuItem key={state.sigla} value={state.sigla}>
											{state.nome}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<FormTextField
							name="cidade"
							label="Cidade"
							value={formData.cidade}
							onChange={handleInputChange}
							disabled={loadings.cep}
							error={Boolean(errors.cidade)}
							helperText={errors.cidade}
						/>
						<FormTextField
							name="bairro"
							label="Bairro"
							value={formData.bairro}
							onChange={handleInputChange}
							disabled={loadings.cep}
							error={Boolean(errors.bairro)}
							helperText={errors.bairro}
						/>
						<FormTextField
							name="logradouro"
							label="Logradouro"
							value={formData.logradouro}
							onChange={handleInputChange}
							disabled={loadings.cep}
							error={Boolean(errors.logradouro)}
							helperText={errors.logradouro}
						/>
						<FormTextField
							name="numero"
							label="Número"
							value={formData.numero}
							onChange={handleInputChange}
							error={Boolean(errors.numero)}
							helperText={errors.numero}
						/>
						<FormTextField
							name="complemento"
							label="Complemento"
							value={formData.complemento}
							onChange={handleInputChange}
						/>

						<Grid item xs={12}>
							<Divider sx={{ my: 1 }} />
						</Grid>

						{/* Contato */}
						<Grid item xs={12}>
							<Typography variant="mySubtitle2">Contato</Typography>
						</Grid>

						<FormTextField
							name="telefone"
							label="Telefone"
							mask={
								formData.telefone?.replace(/\s+/g, "")?.length <= 13
									? "(99) 9999-99999"
									: "(99) 99999-9999"
							}
							value={formData.telefone}
							onChange={handleInputChange}
							onBlur={handleBlur}
							error={Boolean(errors.telefone)}
							helperText={errors.telefone}
						/>
						<FormTextField
							name="email"
							label="Email"
							type="email"
							value={formData.email}
							onChange={handleInputChange}
							onBlur={handleBlur}
							error={Boolean(errors.email)}
							helperText={errors.email}
						/>
					</Grid>
				</Box>

				<Box sx={styles.boxButton}>
					<Button
						disabled={loadings.general}
						variant="contained"
						sx={styles.button}
						size="small"
						onClick={handleSubmit}
					>
						{loadings.general ? <CircularProgress size={24} /> : "Cadastrar"}
					</Button>
				</Box>
			</Paper>
		</Modal>
	);
}

// --- Dados Estáticos ---
const brazilianStates = [
	{ nome: "Acre", sigla: "AC" },
	{ nome: "Alagoas", sigla: "AL" },
	{ nome: "Amapá", sigla: "AP" },
	{ nome: "Amazonas", sigla: "AM" },
	{ nome: "Bahia", sigla: "BA" },
	{ nome: "Ceará", sigla: "CE" },
	{ nome: "Distrito Federal", sigla: "DF" },
	{ nome: "Espírito Santo", sigla: "ES" },
	{ nome: "Goiás", sigla: "GO" },
	{ nome: "Maranhão", sigla: "MA" },
	{ nome: "Mato Grosso", sigla: "MT" },
	{ nome: "Mato Grosso do Sul", sigla: "MS" },
	{ nome: "Minas Gerais", sigla: "MG" },
	{ nome: "Pará", sigla: "PA" },
	{ nome: "Paraíba", sigla: "PB" },
	{ nome: "Paraná", sigla: "PR" },
	{ nome: "Pernambuco", sigla: "PE" },
	{ nome: "Piauí", sigla: "PI" },
	{ nome: "Rio de Janeiro", sigla: "RJ" },
	{ nome: "Rio Grande do Norte", sigla: "RN" },
	{ nome: "Rio Grande do Sul", sigla: "RS" },
	{ nome: "Rondônia", sigla: "RO" },
	{ nome: "Roraima", sigla: "RR" },
	{ nome: "Santa Catarina", sigla: "SC" },
	{ nome: "São Paulo", sigla: "SP" },
	{ nome: "Sergipe", sigla: "SE" },
	{ nome: "Tocantins", sigla: "TO" },
];
